/**
 * Shared infinite-scroll engine for server-rendered post grids (/explore, /l/*, /discussions).
 * Owns the pagination state machine: cursor advance, shortest-column distribution above the
 * placeholder cells, the IntersectionObserver trigger, the end-of-content/auth signup modal,
 * and re-entrancy guards. Page controllers extend it with their paginate path and call
 * setupIntersectionObserver() once their page-specific hooks are wired (ExplorePage
 * instantiates the ad-attribution beacon first — that ordering is load-bearing).
 *
 * Extracted from the previously copy-pasted ExplorePage/DiscussionsPage implementations.
 */
export class PostGridPaginator {
    columns = [];
    AuthErrorCode = 403;
    pageState = null;
    isLoadingContent = false;
    isDisabled = false;
    observer = null;

    // Prefetch distance shared by the observer margin and the manual visibility check — the
    // refill loop only behaves if the two agree. The refill delay keeps the deferred re-check
    // off the current render cycle.
    static PrefetchMarginPx = 400;
    static RefillDelayMs = 250;

    /**
     * Reads pagination state from the server-rendered DOM. paginatePath is the page's JSON
     * pagination endpoint. Columns are discovered from the DOM itself: the shared post-columns
     * partial renders sequential column ids for however many columns the server decided, so
     * there is no separate column-count channel to drift out of sync.
     *
     * Subclasses must not redeclare the inherited state fields — subclass field initializers
     * run after super() and would silently reset the state read here.
     */
    constructor(paginatePath) {
        this.paginatePath = paginatePath;

        // Disable browser scroll restoration to prevent infinite pagination loops on reload
        if ('scrollRestoration' in history) {
            history.scrollRestoration = 'manual';
        }

        // Product: tapping into a post and hitting "Back" should land the reader exactly where they left
        // off — same scroll position, every scrolled-in post still there — never bounced to the top.
        // scrollRestoration='manual' (above) stops WebKit restoring scroll on a bfcache "Back" (iOS Safari
        // brings the DOM back but sits at the top; Blink restores it, so Chrome is fine). Restore it
        // ourselves, only on bfcache (persisted) — DOM is fully back, so no short-page reload-loop risk.
        window.addEventListener('pagehide', () => { this.savedScrollY = window.scrollY; });
        window.addEventListener('pageshow', (e) => {
            if (e.persisted) window.scrollTo(0, this.savedScrollY || 0);
        });

        // Read initial pageState from DOM
        const pageStateEl = document.getElementById("pageState");
        this.pageState = pageStateEl?.dataset.state || null;

        // Discover the server-rendered columns by their sequential ids
        for (let i = 1; ; i++) {
            const col = document.getElementById(`col${i}`);
            if (!col) break;
            this.columns.push(col);
        }
    }

    /**
     * Sets up Intersection Observer to trigger pagination when any placeholder becomes visible.
     * More performant than scroll events and handles edge cases like landing at bottom of page.
     */
    setupIntersectionObserver() {
        this.observer = new IntersectionObserver((entries) => {
            // If any placeholder is visible, try to paginate (getNextPage handles all guards)
            const anyVisible = entries.some(entry => entry.isIntersecting);
            if (anyVisible) {
                this.getNextPage();
            }
        }, {
            // Trigger before the placeholder actually enters the viewport
            rootMargin: `${PostGridPaginator.PrefetchMarginPx}px`,
            threshold: 0
        });

        // Observe all initial placeholder cells
        this.observePlaceholders();
    }

    /**
     * Start observing all placeholder cells in the grid.
     */
    observePlaceholders() {
        const placeholders = document.querySelectorAll('.placeholderCell');
        placeholders.forEach(placeholder => {
            this.observer.observe(placeholder);
        });
    }

    /**
     * Pagination - fetches next page of posts as HTML.
     */
    async getNextPage() {
        if (this.isLoadingContent || this.isDisabled || !this.pageState) {
            return;
        }
        this.isLoadingContent = true;

        try {
            // Request HTML fragments from pagination endpoint
            const response = await fetch(`${this.paginatePath}?pageState=${encodeURIComponent(this.pageState)}`);
            const json = await response.json();

            // Check for errors in response
            if (!json.success) {
                const error = json.error || {};
                if (error.code == this.AuthErrorCode) {
                    this.isLoadingContent = false;
                    this.showSignupAlert();
                    return;
                }
                throw new Error(error.title || "Failed to load content");
            }

            // Update cursor, or stop if no more content
            this.pageState = json.pageState;
            if (!json.hasMore || !json.html) {
                console.log("No more content.");
                this.isLoadingContent = false;
                this.showSignupAlert(); // Sets isDisabled so no further loads run
                return;
            }

            // Parse HTML response into elements
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = json.html;
            const postElements = Array.from(tempDiv.children);

            // Get snapshot of column heights
            let colHeights = this.columns.map(col => col.offsetHeight);

            // Distribute posts into columns using shortest-first algorithm
            for (const postEl of postElements) {
                // Get shortest column
                const shortestColIndex = colHeights.indexOf(Math.min(...colHeights));
                const shortestCol = this.columns[shortestColIndex];

                // Get the first placeholder cell to insert before it (keeps placeholders at bottom)
                const placeholderCell = shortestCol.querySelector('.placeholderCell');

                // Insert post before placeholder cells
                if (placeholderCell) {
                    shortestCol.insertBefore(postEl, placeholderCell);
                } else {
                    shortestCol.appendChild(postEl);
                }

                // Update height estimate (actual height will be known after append)
                colHeights[shortestColIndex] = shortestCol.offsetHeight;
            }

            // Check if we need to load more immediately
            // (Intersection Observer only fires on status change, so if placeholders are still
            // visible after loading, we need to manually trigger another load)
            this.isLoadingContent = false;
            this.checkIfNeedMoreContent();

        } catch (error) {
            console.log(error);
            alert(error.message || error);
            this.isLoadingContent = false;
        }
    }

    /**
     * Checks if any placeholders are currently visible and triggers pagination if needed.
     * Called after loading content to handle cases where loaded content is insufficient.
     */
    checkIfNeedMoreContent() {
        if (this.isLoadingContent || this.isDisabled) {
            return;
        }

        const margin = PostGridPaginator.PrefetchMarginPx;
        const placeholders = document.querySelectorAll('.placeholderCell');
        for (const placeholder of placeholders) {
            const rect = placeholder.getBoundingClientRect();
            const isVisible = rect.top < window.innerHeight + margin && rect.bottom > -margin;
            if (isVisible) {
                // Use setTimeout to avoid blocking the current render cycle
                setTimeout(() => this.getNextPage(), PostGridPaginator.RefillDelayMs);
                return;
            }
        }
    }

    /**
     * Shows the signup modal and disables further pagination — used when content is exhausted
     * or the server requires auth to continue.
     */
    showSignupAlert() {
        this.isDisabled = true;
        const modal = document.getElementById("signupRequiredModal");
        if (modal) {
            // This is a wall, not a dismissible teaser: clear any modal-dismissible a prior
            // comment-teaser open left on the shared node, so the wall can't be Escape/backdrop-closed.
            modal.classList.remove("modal-dismissible");
            modal.classList.add("modal-visible");
        }
    }
}
